import { OnboardingVehicleDto, LabelType } from '@interfaces/models';
import { Typography, Button, toast } from '@ui/components';
import { Edit, User } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useState, Fragment, FC } from 'react';
import { useGetLabels } from '@shared/hooks';

import { EditEmployeeDialog } from '../edit-employees-dialog/edit-employees-dialog';
import { EmployeeFormType } from '../../../hooks';
interface EmployeePreviewProps {
  availableVehicles: OnboardingVehicleDto[];
  employee: EmployeeFormType;
  employeeIndex: number;
  checkIfPhoneNumberIsUnique: (phoneNumber: string, originalPhoneNumber?: string) => boolean;
  handleEditEmployee: (employee: EmployeeFormType) => void;
  handleRemoveEmployee: () => void;
}

export const EmployeePreview: FC<EmployeePreviewProps> = (props) => {
  const {
    availableVehicles,
    employeeIndex,
    employee,
    checkIfPhoneNumberIsUnique,
    handleRemoveEmployee,
    handleEditEmployee,
  } = props;

  const [openEditEmployeeDialog, setOpenEditEmployeeDialog] = useState(false);
  const { t } = useTranslation();

  const { getLabelName } = useGetLabels(LabelType.EMPLOYEE);

  const saveEditedEmployee = (editedEmployee: EmployeeFormType) => {
    const isPhoneNumberUnique = checkIfPhoneNumberIsUnique(
      editedEmployee.phoneNumber,
      employee.phoneNumber,
    );

    if (!isPhoneNumberUnique) {
      toast.error(t('ONBOARDING.ADD_EMPLOYEES.PHONE_NUMBER_NOT_UNIQUE'));
      return;
    }

    handleEditEmployee(editedEmployee);
    setOpenEditEmployeeDialog(false);
  };

  return (
    <>
      <div className="rounded-lg px-4 py-3 border border-border-action mb-8">
        <div className="flex items-center gap-2 mb-4">
          <User className="size-4" />
          <Typography variant="h100">
            {t('ONBOARDING.ADD_EMPLOYEES.EMPLOYEE')} {employeeIndex + 1}
          </Typography>

          <Button
            onClick={() => setOpenEditEmployeeDialog(true)}
            className="ml-auto"
            variant="ghost"
          >
            <Edit className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Typography
            className="truncate [&>span:last-child]:text-transparent"
            variant="labelMediumBook"
          >
            {employee.firstName} <span className="px-2 text-border-default">|</span>
            {employee.lastName} <span className="px-2 text-border-default">|</span>
            {employee.phoneExt} {employee.phoneNumber}{' '}
            <span className="px-2 text-border-default">|</span>
            {employee.email && (
              <>
                {employee.email} <span className="px-2 text-border-default">|</span>
              </>
            )}
            {employee.labels?.map((label) => {
              const labelName = getLabelName(label);
              return (
                <Fragment key={label}>
                  {labelName} <span className="px-2 text-border-default">|</span>
                </Fragment>
              );
            })}
            {employee.vehicles?.map((vehicle) => {
              const vehicleDetails = availableVehicles.find((v) => v.id === vehicle);
              return (
                <Fragment key={vehicleDetails?.registrationNumber}>
                  {vehicleDetails?.registrationNumber}
                  <span className="px-2 text-border-default">|</span>
                </Fragment>
              );
            })}
          </Typography>
        </div>

        <EditEmployeeDialog
          onOpenChange={setOpenEditEmployeeDialog}
          availableVehicles={availableVehicles}
          onRemove={handleRemoveEmployee}
          open={openEditEmployeeDialog}
          onSave={saveEditedEmployee}
          employee={employee}
        />
      </div>
    </>
  );
};
